import React, { useState, useEffect } from "react";

import CustomSelect from "../ui/CustomSelect";
/**
 * MonthYearSelector component - Clean month/year picker
 * @param {Object} props
 * @param {Function} props.onChange - Callback with {year, month}
 * @param {number} props.selectedYear - Currently selected year
 * @param {number} props.selectedMonth - Currently selected month (1-12)
 * @param {boolean} props.showCurrentOption - Show "Current" option
 */
const MonthYearSelector = ({
  onChange,
  selectedYear,
  selectedMonth,
  showCurrentOption = true,
  compact = false,
}) => {
  const [isCurrent, setIsCurrent] = useState(!selectedYear || !selectedMonth);

  // Sync isCurrent state with props
  useEffect(() => {
    setIsCurrent(!selectedYear || !selectedMonth);
  }, [selectedYear, selectedMonth]);

  // Generate years (last 5 years to next year)
  const now = new Date();
const currentYear = now.getUTCFullYear();
const currentMonth = now.getUTCMonth() + 1;
  const years = [];
  for (let i = currentYear - 5; i <= currentYear + 1; i++) {
    years.push(i);
  }

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  useEffect(() => {
    if (isCurrent) {
      onChange(null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrent]);

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setIsCurrent(false);
    onChange(year, selectedMonth || currentMonth);
  };

  const handleMonthChange = (e) => {
    const month = parseInt(e.target.value);
    setIsCurrent(false);
    onChange(selectedYear || currentYear, month);
  };

  const handleCurrentToggle = () => {
    setIsCurrent(true);
  };

  return (
    <div
      className={
        compact
          ? "flex flex-wrap items-center gap-1.5"
          : "flex flex-wrap items-center gap-2 sm:gap-3 bg-white border border-gray-200 rounded-md py-2 px-3 shadow-sm"
      }
    >
      {showCurrentOption && (
        <label className={`flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded transition-colors ${compact ? "px-1 py-0.5" : "px-2 py-1.5"}`}>
          <input
            type="radio"
            name="reportType"
            checked={isCurrent}
            onChange={handleCurrentToggle}
            className="w-4 h-4 text-[var(--accent,#a51c1c)] border-gray-300 focus:ring-[var(--accent,#a51c1c)]"
          />
          <span className={`font-medium text-gray-700 ${compact ? "text-[12px]" : "text-sm"}`}>
            Current
          </span>
        </label>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <label className={`flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded transition-colors ${compact ? "px-1 py-0.5" : "px-2 py-1.5"}`}>
          <input
            type="radio"
            name="reportType"
            checked={!isCurrent}
            onChange={() => {
              setIsCurrent(false);
              // Ensure parent receives a concrete month when switching from Current (props may be null)
              onChange(
                selectedYear || currentYear,
                selectedMonth || currentMonth,
              );
            }}
            className="w-4 h-4 text-[var(--accent,#a51c1c)] border-gray-300 focus:ring-[var(--accent,#a51c1c)]"
          />
          <span className={`font-medium text-gray-700 ${compact ? "text-[12px]" : "text-sm"}`}>
            Historical
          </span>
        </label>

        <CustomSelect
          value={selectedYear || currentYear}
          onChange={handleYearChange}
          disabled={isCurrent}
          className={`border border-[var(--line-strong,#d5d3cc)] rounded font-medium text-[var(--ink,#1c1c1a)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)] disabled:bg-[var(--sunken,#eeece7)] disabled:cursor-not-allowed ${
            compact ? "px-2 py-1 text-[12px]" : "px-2.5 py-1.5 text-sm"
          }`}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </CustomSelect>
        <CustomSelect
          value={selectedMonth || currentMonth}
          onChange={handleMonthChange}
          disabled={isCurrent}
          className={`border border-[var(--line-strong,#d5d3cc)] rounded font-medium text-[var(--ink,#1c1c1a)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)] disabled:bg-[var(--sunken,#eeece7)] disabled:cursor-not-allowed min-w-[120px] ${
            compact ? "px-2 py-1 text-[12px]" : "px-2.5 py-1.5 text-sm"
          }`}
        >
          {months.map((month) => (
            <option key={month.value} value={month.value}>
              {month.label}
            </option>
          ))}
        </CustomSelect>
      </div>
    </div>
  );
};

export default MonthYearSelector;
